import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";
import "./ReportsCategories.scss";
import CategoryModal from "../report-category-modal";
import { axiosRestInstance } from "../../../../../common/axios";
import ErrorPopup from "../error-popup";
import DeleteModal from "../../../../../common/components/DeleteModal";

type CategoryNavigation = {
  key: string;
  title: string;
  description: string;
  selectedCategoryKey: string;
};

type Props = {
  onOpenCategory: (category: {
    key: string;
    title: string;
    description: string;
    selectedCategoryKey: string;
  }) => void;
  onCategoriesLoaded?: (count: number) => void;
  selectedCategoryKey?: string;
  /** Hidden when category_management is view or na. */
  categoryManagementEdit?: boolean;
  categoryManagementReadOnly?: boolean;
};

type Stat = {
  label: string;
  value: number | string;
  iconAria: string;
  icon: ReactNode;
};

export type Category = {
  id: string; // ✅ add this
  key: string;
  title: string;
  description: string;
  reports: number;
  canView: number;
  color: string;
  bgColor: string;
  iconKey: string;
  viewOnlyRoles: string[];
  editRoles: string[];
  created_at?: string;
};

/* ---------------- API CALLS ---------------- */

export const getAllCategoryAPI = async (): Promise<any> => {
  try {
    const response = await axiosRestInstance.get("/category/all");
    //console.log("category", response);
    return response.data;
  } catch (error) {
    console.error("Error fetching categories:", error);
    return null;
  }
};

export const updateCategoryAPI = async (id: string, payload: any): Promise<any> => {
  try {
    const response = await axiosRestInstance.put(`/category/${id}`, payload);
    return response.data;
  } catch (error: unknown) {
      const err = error as {
        response?: {
          data?: {
            message?: string;
          };
        };
      };

      return {
        success: false,
        message:
          err?.response?.data?.message ||
          "Category name already exists",
      };
    }
};

// API call for deleting category
export const deleteCategoryAPI = async (categoryId: string): Promise<any> => {
  try {
    const response = await axiosRestInstance.delete(`/category/${categoryId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting category:", error);
    return { success: false, message: "Error deleting category" };
  }
};

/* ---------------- COMPONENTS ---------------- */

const StatTile: React.FC<{ stat: Stat }> = ({ stat }) => (
  <div className="gdl-stat">
    <div className="gdl-stat__content">
      <div className="gdl-stat__label">{stat.label}</div>
      <div className="gdl-stat__value">{stat.value}</div>
    </div>
    <div className="gdl-stat__icon">{stat.icon}</div>
  </div>
);

const CategoryCard: React.FC<{
  item: Category;
  onEdit: (item: Category) => void;
  onOpen: (item: Category) => void;
  onDelete: (item: Category) => void;
  isActive?: boolean;
  showCategoryActions?: boolean;
}> = ({
  item,
  onEdit,
  onOpen,
  onDelete,
  isActive,
  showCategoryActions = true,
}) => (
  <article className={`gdl-card ${isActive ? "gdl-card--active" : ""}`}>
    <header className="gdl-card__top">
      <div className="gdl-card__icon" style={{ backgroundColor: item.bgColor, color: item.color }}>
        <Iconography icon={item.iconKey} size={IconographySizes.Large} />
      </div>
      <button className="gdl-card__arrow" title="Category Detail" onClick={() => onOpen(item)}>
        <Iconography icon="arrowRight" size={IconographySizes.Small} />
      </button>
    </header>

    <div className="gdl-card__body">
      <h3 className="gdl-card__title">{item.title}</h3>
      <p className="gdl-card__desc">{item.description}</p>
    </div>

    <footer className="gdl-card__footer">
      <span className="gdl-pill">{item.reports} Reports</span>
      <span className="gdl-pill">{item.canView} Can View</span>

      {showCategoryActions && (
        <>
          <button
            type="button"
            className="gdl-card__edit"
            title="Category Edit"
            onClick={() => onEdit(item)}
          >
            <Iconography icon="pen" size={IconographySizes.Small} />
          </button>
          <button
            type="button"
            className="gdl-card__delete"
            title="Category Delete"
            onClick={() => onDelete(item)}
          >
            <Iconography icon="trashCan" size={IconographySizes.Small} />
          </button>
        </>
      )}
    </footer>
  </article>
);

/* ---------------- MAIN COMPONENT ---------------- */

const ReportCategories: React.FC<Props> = ({
  onOpenCategory,
  onCategoriesLoaded,
  selectedCategoryKey,
  categoryManagementEdit = true,
  categoryManagementReadOnly = false,
}) => {
  const showCategoryActions = categoryManagementEdit;
  const canEditCategory = categoryManagementEdit;
  const [categories, setCategories] = useState<Category[]>([]);
  const [editing, setEditing] = useState<Category | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false); // State for DeleteModal
  const [deleteButtonLoading, setDeleteButtonLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null); // Category to delete
  const [loading, setLoading] = useState(false);

  // NEW STATE: error popup
  const [popup, setPopup] = useState({
    open: false,
    message: "",
    type: "error" as "success" | "error",
  });

  const [meta, setMeta] = useState<{
    total_reports: number;
    reports_generated_today: number;
    total_categories: number;
  } | null>(null);

  const handleGetAllCategories = async () => {
    setLoading(true);
    const result = await getAllCategoryAPI();
    if (!result?.success) {
      setCategories([]);
      setMeta(null);
      onCategoriesLoaded?.(0);
      setLoading(false);
      return;
    }

    // STORE META
    setMeta(result.meta);

    const mappedCategories: Category[] = result.data.map((cat: any) => {
      const viewRoles = Array.isArray(cat.user_view)
        ? cat.user_view.map((r: any) => (typeof r === "string" ? r : r.role))
        : [];

      const editRoles = Array.isArray(cat.user_edit)
        ? cat.user_edit.map((r: any) => (typeof r === "string" ? r : r.role))
        : [];

      return {
        id: cat.ID.toString(),
        key: cat.ID.toString(),
        title: cat.name,
        description: cat.description,
        reports: cat.report_count ?? 0,
        canView: viewRoles.length,
        bgColor: "#E6EDF6",
        color: "#003087",
        iconKey: "folderBookmark",
        viewOnlyRoles: viewRoles,
        editRoles,
        created_at: cat.created_at,
      };
    });

    setCategories(mappedCategories);
    onCategoriesLoaded?.(mappedCategories.length);
    setLoading(false);
  };

  // Delete category
  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    setDeleteButtonLoading(true);  // Show loading state for delete button
    const result = await deleteCategoryAPI(categoryToDelete.id);

    if (result.success) {
      // Remove deleted category from the list
      const filterCategories = categories.filter((cat) => cat.key !== categoryToDelete.key);
      setCategories(filterCategories);
      onCategoriesLoaded(filterCategories.length);

      // Update meta in real time
      setMeta((prev) =>
        prev
          ? {
              ...prev,
              total_categories: Math.max(0, prev.total_categories - 1),
            }
          : prev
      );

      // Close DeleteModal after a short delay to ensure modal state is updated
      setTimeout(() => {
        setDeleteOpen(false);  // Close the DeleteModal
      }, 300);
      
      // Show success popup
      setPopup({
        open: true,
        message: "Category deleted successfully.",
        type: "success",
      });
    } else {
      // Show error popup if deletion failed
      setPopup({
        open: true,
        message: result.message || "Failed to delete category.",
        type: "error",
      });
    }
    setDeleteButtonLoading(false);  // Reset loading state for delete button
  };


  useEffect(() => {
    handleGetAllCategories();
  }, []);

  const handleSave = async (
    data: { name: string; description: string; viewOnlyRoles: string[]; editRoles: string[] },
    key: string
  ) => {
    const categoryId = key.replace("category-", "");

    // BACKEND-COMPATIBLE PAYLOAD
    const payload = {
      name: data.name,
      description: data.description,
      user_view: data.viewOnlyRoles,
      user_edit: data.editRoles,
    };

    const result = await updateCategoryAPI(categoryId, payload);
    // CHANGE: show popup on backend error
    // Show error popup if something goes wrong
    if (!result?.success) {
      setPopup({
        open: true,
        message: result.message,
        type: "error",
      });
      return;
    }

    setCategories((prev) =>
      prev.map((cat) =>
        cat.key === key
          ? {
            ...cat,
            title: data.name,
            description: data.description,
            viewOnlyRoles: [...data.viewOnlyRoles],
            editRoles: [...data.editRoles],
            canView: data.viewOnlyRoles.length,
          }
          : cat
      )
    );

    setEditing(null);

    // Show success message
    setPopup({
      open: true,
      message: "Category updated successfully.",
      type: "success",
    });
  };

  const stats: Stat[] = [
    {
      label: "Total Report",
      value: meta?.total_reports ?? 0,
      iconAria: "Total report icon",
      icon: <Iconography icon="file" size={IconographySizes.Large} />,
    },
    {
      label: "Total Category",
      value: meta?.total_categories ?? categories.length,
      iconAria: "Total categories icon",
      icon: <Iconography icon="folderBookmark" size={IconographySizes.Large} />,
    },
    {
      label: "Generated Today",
      value: meta?.reports_generated_today ?? 0,
      iconAria: "Generated Today icon",
      icon: <Iconography icon="circleCalendar" size={IconographySizes.Large} />,
    },
    {
      label: "Total Requests for Report",
      value: 4,
      iconAria: "Total Requests for Report icon",
      icon: <Iconography icon="messageArrowDown" size={IconographySizes.Large} />,
    },
  ];

  return (
    <>
    {/* NEW ERROR POPUP */}
    <ErrorPopup
      open={popup.open}
      message={popup.message}
      type={popup.type}
      onClose={() => setPopup({ open: false, message: "", type: "error" })}
    />

    {/* Delete Category Modal */}
    {categoryToDelete && deleteOpen && (
      <DeleteModal
        setDeleteOpen={setDeleteOpen}  // Control visibility inside DeleteModal
        onDeleteButtonClick={handleDeleteCategory}
        deleteButtonLoading={deleteButtonLoading}
        message={`This will delete the category permanently.`}
      />
    )}

    {loading ? (
      <div className="gdl-page-loader">
        <Loader variant={LoaderSizes.large} />
      </div>
    ) : (
      <section className="gdl-categories">
        <div className="gdl-stats">{stats.map((s) => <StatTile key={s.label} stat={s} />)}</div>

        <h2 className="gdl-section-title">Report Categories</h2>

        <div className="gdl-grid">
          {categories.map((c) => (
            <CategoryCard
              key={c.key}
              item={c}
              onEdit={setEditing}
              onOpen={(cat) =>
                onOpenCategory({
                  key: cat.id,
                  title: cat.title,
                  description: cat.description,
                  selectedCategoryKey: cat.id,
                })
              }
              onDelete={(item) => {
                setCategoryToDelete(item);
                setDeleteOpen(true);
              }}
              isActive={selectedCategoryKey === c.key || selectedCategoryKey === c.id}
              showCategoryActions={showCategoryActions}
            />
          ))}
        </div>

        {editing && canEditCategory && (
          <CategoryModal
            mode="edit"
            open={true}
            readOnly={categoryManagementReadOnly}
            initialData={{
              name: editing.title,
              description: editing.description,
              viewOnlyRoles: editing.viewOnlyRoles,
              editRoles: editing.editRoles,
            }}
            onClose={() => setEditing(null)}
            onSubmit={(data) => handleSave(data, editing.key)}
          />
        )}
      </section>
    )}

    </>
  );
};

export default ReportCategories;
